import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import useListenSize from '../../hooks/useListenSize';
import loadingSVG from '../../assets/img/loading.svg';

export function AgreementChecklistItem({
  index,
  title,
  contractHtml,
  loading,
  isAcknowledged,
  onAcknowledge,
  defaultOpen,
  requireScrollToAcknowledge,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isClosing, setIsClosing] = useState(false);
  const contractRef = useRef(null);
  // Tracks "currently sitting at the bottom", not "has ever acknowledged" - resets once the
  // user scrolls away, so a rejected acknowledgment (e.g. a failed enrollment call the parent
  // declines to accept) can be retried by scrolling away and back to the bottom again, instead
  // of being permanently stuck the one time this fires.
  const wasAtBottomRef = useRef(false);
  const collapseTimeoutRef = useRef(null);
  const closeAnimTimeoutRef = useRef(null);
  // Latest isAcknowledged, readable from inside the collapse timeout's closure below (which
  // captures whatever isAcknowledged was at the moment it was scheduled, not what it becomes
  // once an async onAcknowledge call actually settles).
  const isAcknowledgedRef = useRef(isAcknowledged);
  useEffect(() => {
    isAcknowledgedRef.current = isAcknowledged;
  }, [isAcknowledged]);

  useListenSize(isOpen);

  // Delay before auto-collapsing after scroll-to-acknowledge, so the checkmark
  // registers visually before the accordion closes.
  const AUTO_COLLAPSE_DELAY_MS = 500;
  // Duration of the collapse animation; keep in sync with the CSS animation.
  const CLOSE_ANIM_MS = 300;

  // Play the collapse animation, then actually unmount the body.
  const animateCollapse = () => {
    setIsClosing(true);
    closeAnimTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, CLOSE_ANIM_MS);
  };

  const checkboxId = `agreement-checkbox-${index}`;

  const tryAutoAcknowledge = () => {
    if (!requireScrollToAcknowledge || isAcknowledged) {
      return;
    }
    const el = contractRef.current;
    if (!el) {
      return;
    }
    // Same threshold/formula this codebase already uses for scroll-to-bottom detection
    // elsewhere (the pre-rewrite LeaseAgreement.js's handleScroll).
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    if (!atBottom) {
      wasAtBottomRef.current = false;
      return;
    }
    if (wasAtBottomRef.current) {
      return;
    }
    wasAtBottomRef.current = true;
    onAcknowledge(true);
    // Collapse this item once its contract has actually been acknowledged (checked at fire
    // time, not scheduling time - onAcknowledge above may be async and end up not acknowledging
    // at all, e.g. a failed enrollment call the parent declines to accept), after a short delay
    // so the acknowledgement (checkmark) is visible first, then animate it closed.
    collapseTimeoutRef.current = setTimeout(() => {
      if (isAcknowledgedRef.current) {
        animateCollapse();
      }
    }, AUTO_COLLAPSE_DELAY_MS);
  };

  // Clear any pending auto-collapse / close-animation timers on unmount.
  useEffect(
    () => () => {
      clearTimeout(collapseTimeoutRef.current);
      clearTimeout(closeAnimTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isOpen && !loading && contractHtml) {
      // Content shorter than the box's max-height never fires a scroll event at all, so check
      // once after layout too - otherwise a short contract could never be acknowledged.
      const timeoutId = setTimeout(tryAutoAcknowledge, 0);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isOpen, loading, contractHtml]);

  return (
    <div className={`k-agreement-item${isAcknowledged ? ' k-agreement-item--done' : ''}`}>
      <button
        type="button"
        className="k-agreement-item__header"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
      >
        <span className={`k-agreement-item__circle${isAcknowledged ? ' k-agreement-item__circle--done' : ''}`}>
          {isAcknowledged ? '✓' : index}
        </span>
        <span className="k-agreement-item__title">{title}</span>
        <span
          className={`k-agreement-item__chevron${isOpen ? ' k-agreement-item__chevron--open' : ''}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className={`k-agreement-item__body${isClosing ? ' k-agreement-item__body--closing' : ''}`}>
          {loading && <img alt="" className="k-loading-icon" src={loadingSVG} />}
          {!loading && contractHtml && (
            <div
              className="k-agreement-item__contract"
              ref={contractRef}
              onScroll={requireScrollToAcknowledge ? tryAutoAcknowledge : undefined}
            >
              {parse(contractHtml)}
            </div>
          )}
          {!requireScrollToAcknowledge && (
            <label className="k-agreement-item__checkbox-row" htmlFor={checkboxId}>
              <input
                type="checkbox"
                id={checkboxId}
                checked={isAcknowledged}
                onChange={e => onAcknowledge(e.target.checked)}
              />
              I have read and agree to the {title}
            </label>
          )}
        </div>
      )}
    </div>
  );
}

AgreementChecklistItem.propTypes = {
  index: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  contractHtml: PropTypes.string,
  loading: PropTypes.bool,
  isAcknowledged: PropTypes.bool.isRequired,
  onAcknowledge: PropTypes.func.isRequired,
  defaultOpen: PropTypes.bool,
  requireScrollToAcknowledge: PropTypes.bool,
};

AgreementChecklistItem.defaultProps = {
  contractHtml: '',
  loading: false,
  defaultOpen: false,
  requireScrollToAcknowledge: false,
};

export default AgreementChecklistItem;
